import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const ToastNotification = ({ 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose,
  position = 'top-right'
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose?.(), 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const typeConfig = {
    success: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-800',
      icon: <CheckCircle className="w-5 h-5 text-emerald-500" />,
      progressBg: 'bg-emerald-500'
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: <AlertCircle className="w-5 h-5 text-red-500" />,
      progressBg: 'bg-red-500'
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
      progressBg: 'bg-amber-500'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: <Info className="w-5 h-5 text-blue-500" />,
      progressBg: 'bg-blue-500'
    }
  };

  const config = typeConfig[type] || typeConfig.info;

  if (!isVisible) return null;

  return (
    <div className={`fixed z-50 ${position === 'top-right' ? 'top-4 right-4' : 'top-4 left-4'} animate-slide-in-right`}>
      <div className={`w-96 max-w-full rounded-xl border ${config.bg} ${config.border} p-4 shadow-lg`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">{config.icon}</div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${config.text}`}>{message}</p>
          </div>
          <button
            onClick={() => {
              setIsVisible(false);
              setTimeout(() => onClose?.(), 300);
            }}
            className="flex-shrink-0 p-1 hover:bg-white/50 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        {/* Progress bar */}
        {duration > 0 && (
          <div className="mt-2 w-full h-1 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${config.progressBg} rounded-full transition-all duration-[3000ms] ease-linear`}
              style={{ width: '100%' }}
              onAnimationEnd={() => {
                setIsVisible(false);
                setTimeout(() => onClose?.(), 300);
              }}
              key={Date.now()}
            >
              <style jsx>{`
                @keyframes shrink {
                  from { width: 100%; }
                  to { width: 0%; }
                }
                .progress-bar {
                  animation: shrink ${duration}ms linear forwards;
                }
              `}</style>
              <div className="progress-bar h-full" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToastNotification;